/**
 * Tour step content in DE / EN / IT.
 * Each key maps to an object with title and body strings.
 */
const CONTENT = {
  de: {
    // Welcome
    welcome_title: "Willkommen in der TreeSense Cloud",
    welcome_body:
      "Diese Tour zeigt Ihnen in wenigen Minuten die wichtigsten Funktionen der Plattform – von der Übersicht bis zur Datenanalyse.",
    welcome_tip: "Sie können die Tour jederzeit mit dem ✕ schließen.",

    // Dashboard navigation step
    nav_dashboard_title: "Dashboard",
    nav_dashboard_body:
      "Das Dashboard bündelt alle wichtigen Kennzahlen auf einen Blick. Es bezieht sich auf das im Global-Filter ausgewählte Projekt.",

    // Dashboard steps
    dashboard_overview_title: "Dashboard – Übersicht",
    dashboard_overview_body:
      "Hier sehen Sie alle KPIs Ihres Baumbestands, der Sensorik und der Aufgaben auf einen Blick.",
    dashboard_inventory_title: "Baumbestand",
    dashboard_inventory_body:
      "Anzahl der Bäume und Baumarten im aktuellen Projekt. Klicken Sie auf Bäume anzeigen, um direkt in das Baumkataster zu springen.",
    dashboard_biometrics_title: "Baumbiometrie",
    dashboard_biometrics_body:
      "Durchschnittswerte für Höhe, Stammumfang, Kronendurchmesser und Alter – hilfreich zur schnellen Plausibilitätsprüfung Ihrer Daten.",
    dashboard_sensors_title: "Sensorik & Status",
    dashboard_sensors_body:
      "Anzahl der Sensoren sowie Hinweise auf kritische Zustände (leere Batterie, getrennte Sensoren). Klicken Sie auf Sensoren anzeigen, um zur Sensorliste zu wechseln.",
    dashboard_health_title: "Baumgesundheit",
    dashboard_health_body:
      "Das Donut-Diagramm zeigt die Verteilung der Gesundheitszustände Ihrer Bäume. Nutzen Sie diese Ansicht zur schnellen Priorisierung von Kontrollen und Bewässerung.",
    dashboard_tasks_title: "Aufgaben nach Zeitraum",
    dashboard_tasks_body:
      "Wie viele Aufgaben stehen heute, diese Woche oder diesen Monat an? Mit Zu den Aufgaben öffnen Sie die Aufgabenverwaltung.",

    // Trees navigation step
    nav_trees_title: "Baumkataster",
    nav_trees_body:
      "Im Baumkataster erfassen und verwalten Sie alle Bäume – mit Standort, Bauminformationen und Sensorenzuordnung.",

    // Trees steps
    tree_list_title: "Baumliste",
    tree_list_body:
      "Alle Bäume des ausgewählten Projekts in einer durchsuchbaren, sortierbaren Tabelle. Die Statusspalte zeigt den aktuellen Gesundheitszustand.",
    tree_add_title: "Baum hinzufügen",
    tree_add_body:
      "Klicken Sie auf + Baum hinzufügen, um einen neuen Baum anzulegen. Im Erstellungsformular wählen Sie Projekt, Bereich und erfassen Bauminformationen sowie den Standort auf der Karte.",

    // Tree map navigation step
    nav_map_title: "Baumkarte",
    nav_map_body:
      "Die Kartenansicht zeigt alle Bäume mit ihrem Standort auf einer interaktiven Karte. Farbe und Symbol geben Aufschluss über Gesundheitszustand und Sensorstatus.",

    // Tree map step
    tree_map_title: "Interaktive Baumkarte",
    tree_map_body:
      "Klicken Sie auf einen Baum-Pin, um die Detailansicht zu öffnen. Über die Ebenensteuerung oben rechts wechseln Sie zwischen Karten- und Satellitenbild.",

    // Map: satellite layer
    map_satellite_title: "Satellitenbild aktivieren",
    map_satellite_body:
      "Mit der Ebenensteuerung oben rechts wechseln Sie zur Satellitenansicht. Das gibt Ihnen einen besseren Überblick über die reale Umgebung der Bäume. Wir schalten jetzt auf Satellit um.",

    // Map: add tree
    map_add_tree_title: "Baum hinzufügen",
    map_add_tree_body:
      "Mit dem Button Baum hinzufügen unten rechts starten Sie das Erfassen eines neuen Baums. Klicken Sie auf die Karte, um den Standort zu setzen, und füllen Sie anschließend das Formular aus.",

    // Sensors navigation step
    nav_sensors_title: "Sensormonitoring",
    nav_sensors_body:
      "In der Sensorliste sehen Sie alle Sensoren des Projekts – mit Status, DevEUI/IMEI und letztem Batteriewert.",

    // Sensors steps
    sensor_list_title: "Sensorliste",
    sensor_list_body:
      "Nutzen Sie Suche und Sortierung, um auffällige Sensoren schnell zu finden. Ein Klick auf einen Sensor öffnet die Detailansicht mit den letzten 10 Messwerten, Grenzwert und Batteriestatus.",

    // Project filter step (before data science)
    project_filter_title: "Globaler Projektfilter",
    project_filter_body:
      "Mit dem Projektfilter in der Navigationsleiste schränken Sie die Ansicht auf ein bestimmtes Projekt ein. Wir wählen jetzt das erste Projekt für die Datenanalyse aus.",

    // Data Analysis navigation step
    nav_datascience_title: "Datenanalyse",
    nav_datascience_body:
      "Analysieren Sie Sensor-Zeitreihen (z.B. Widerstand, Feuchtigkeitsindex) über beliebige Zeiträume und vergleichen Sie diese mit Niederschlagsdaten.",

    // Data Analysis steps
    datascience_filters_title: "Filter & Variablen",
    datascience_filters_body:
      "Der linke Filterbereich zeigt Bereiche, Bäume, Sensoren und Variablen. Wählen Sie hier schrittweise aus, was Sie analysieren möchten.",

    ds_filter_areas_title: "Schritt 1 – Bereich auswählen",
    ds_filter_areas_body:
      "Aktivieren Sie einen oder mehrere Bereiche, um die zugehörigen Bäume zu laden. Wir wählen jetzt den ersten Bereich aus.",

    ds_filter_trees_title: "Schritt 2 – Baum auswählen",
    ds_filter_trees_body:
      "Nach der Bereichsauswahl erscheinen die Bäume des Bereichs. Wählen Sie einen oder mehrere Bäume aus, um deren Sensoren zu sehen.",

    ds_filter_sensors_title: "Schritt 3 – Sensor auswählen",
    ds_filter_sensors_body:
      "Wählen Sie den Sensor aus, dessen Messwerte Sie analysieren möchten. Jeder Sensor liefert spezifische Variablen.",

    ds_filter_variables_title: "Schritt 4 – Variable auswählen",
    ds_filter_variables_body:
      "Aktivieren Sie eine oder mehrere Variablen (z.B. Widerstand, Feuchtigkeitsindex), um die Kurven im Diagramm anzuzeigen. Ziehen Sie Variablen auf die linke oder rechte Y-Achse.",

    datascience_date_title: "Zeitraum-Filter",
    datascience_date_body:
      "Rechts wählen Sie den Analysezeitraum: von 1 Tag bis 1 Jahr. Klicken Sie anschließend auf Daten laden, um die Grafiken zu aktualisieren.",

    ds_load_data_title: "Daten laden",
    ds_load_data_body:
      "Mit Daten laden werden alle ausgewählten Sensordaten für den gewählten Zeitraum abgerufen und im Diagramm dargestellt. Wir starten den Ladevorgang jetzt.",

    // Tasks navigation step
    nav_tasks_title: "Aufgaben",
    nav_tasks_body:
      "Die Aufgabenverwaltung verbindet Monitoring und operative Umsetzung. Listenansicht für Filter/Suche, Kalenderansicht für die Monatsplanung.",

    // Tasks steps
    task_header_title: "Aufgaben – Übersicht",
    task_header_body:
      "Filtern Sie Aufgaben nach Typ, Zeitraum, Status und Priorität. Suchen Sie gezielt nach bestimmten Aufgaben.",
    task_calendar_title: "Kalenderansicht",
    task_calendar_body:
      "Wechseln Sie zur Kalenderansicht, um Aufgaben im Monatsüberblick zu planen und Lastspitzen sichtbar zu machen.",
    task_add_title: "Aufgabe anlegen",
    task_add_body:
      "Klicken Sie auf + Neue Aufgabe, um eine Aufgabe manuell anzulegen – z.B. Bewässerung, Regelkontrolle oder Schnittmaßnahme. Wählen Sie Baum, Typ, Datum und verantwortliche Person.",

    // Projects navigation step
    nav_projects_title: "Projekte",
    nav_projects_body:
      "Projekte bündeln Bäume, Sensoren und Aufgaben. Das Projektdashboard zeigt Aufgabenverteilungen, Baumphasen und Gesundheitsdiagramme.",

    // Projects steps
    projects_grid_title: "Projektübersicht",
    projects_grid_body:
      "Alle Ihre Projekte auf einen Blick. Klicken Sie auf Projekt anzeigen, um in die Projektdetailansicht mit KPIs, Karte und Datenanalyse zu wechseln.",

    projects_open_title: "Projekt öffnen",
    projects_open_body:
      "Wir öffnen jetzt das erste Projekt. In der Detailansicht finden Sie Karte, Zeitreihen, Aufgaben und alle KPIs des Projekts – alles auf einen Blick.",

    // Settings navigation step
    nav_settings_title: "Einstellungen",
    nav_settings_body:
      "Verwalten Sie Benachrichtigungen, Sprache, Benutzer:innen und Kundendaten – alles in den Einstellungen.",

    // Settings steps
    settings_page_title: "Einstellungen – Überblick",
    settings_page_body:
      "Hier konfigurieren Sie E-Mail-Benachrichtigungen, wählen die Sprache (Deutsch/Englisch/Italiano), verwalten Benutzer:innen und pflegen das Kundenlogo.",

    // Settings users navigation step
    nav_settings_users_title: "Nutzerverwaltung",
    nav_settings_users_body:
      "Wechseln wir zur Nutzerverwaltung, wo Sie Teammitglieder anlegen und deren Zugriffsrechte festlegen können.",

    // Settings users step
    settings_users_title: "Nutzer hinzufügen & verwalten",
    settings_users_body:
      "Hier sehen Sie alle Nutzer des Kontos. Über + Nutzer hinzufügen legen Sie neue Konten an.",

    settings_add_user_btn_title: "Nutzer hinzufügen",
    settings_add_user_btn_body:
      "Klicken Sie auf + Nutzer hinzufügen, um das Formular zu öffnen. Geben Sie Name, E-Mail-Adresse und die gewünschte Persona ein.",

    user_persona_inspector_title: "Persona: Tree Inspector",
    user_persona_inspector_body:
      "Diese Persona ist für Baumkontrollen und operative Feldarbeit ausgelegt. Sie arbeitet vor allem mit Baumkataster, Aufgaben und Vor-Ort-Dokumentation.",

    user_persona_manager_title: "Persona: City Tree Manager",
    user_persona_manager_body:
      "Diese Persona ist für Management und Überblick gedacht. Fokus liegt auf Dashboard, Analysen und strategischer Planung.",

    // Finish
    finish_title: "Tour abgeschlossen",
    finish_body:
      "Sie kennen jetzt die wichtigsten Funktionen der TreeSense Cloud. Nutzen Sie das Handbuch für detaillierte Schritt-für-Schritt-Anleitungen. Viel Erfolg!",
    finish_close: "Tour beenden",
  },

  en: {
    welcome_title: "Welcome to TreeSense Cloud",
    welcome_body:
      "This tour will show you the key features of the platform in just a few minutes – from the overview to data analysis.",
    welcome_tip: "You can close the tour at any time with the ✕ button.",

    nav_dashboard_title: "Dashboard",
    nav_dashboard_body:
      "The dashboard consolidates all key metrics at a glance. It always reflects the project selected in the Global Filter.",

    dashboard_overview_title: "Dashboard – Overview",
    dashboard_overview_body:
      "See all KPIs for your tree inventory, sensors and tasks in one place.",
    dashboard_inventory_title: "Tree Inventory",
    dashboard_inventory_body:
      "Number of trees and species in the current project. Click View Trees to jump directly to the tree cadastre.",
    dashboard_biometrics_title: "Tree Biometrics",
    dashboard_biometrics_body:
      "Average values for height, trunk circumference, crown diameter and age – useful for quickly checking the plausibility of your data.",
    dashboard_sensors_title: "Sensors & Status",
    dashboard_sensors_body:
      "Number of sensors and alerts for critical states (low battery, disconnected sensors). Click View Sensors to go to the sensor list.",
    dashboard_health_title: "Tree Health",
    dashboard_health_body:
      "The donut chart shows the distribution of health statuses of your trees. Use this view to quickly prioritise inspections and irrigation.",
    dashboard_tasks_title: "Tasks by Period",
    dashboard_tasks_body:
      "How many tasks are due today, this week or this month? Click Go to Tasks to open task management.",

    nav_trees_title: "Tree Cadastre",
    nav_trees_body:
      "The tree cadastre is where you record and manage all trees – with location, tree data and sensor assignments.",

    tree_list_title: "Tree List",
    tree_list_body:
      "All trees of the selected project in a searchable, sortable table. The status column shows the current health state.",
    tree_add_title: "Add Tree",
    tree_add_body:
      "Click + Add Tree to create a new tree. In the form you select the project, area and enter tree data plus the location on the map.",

    nav_map_title: "Tree Map",
    nav_map_body:
      "The map view shows all trees with their location on an interactive map. Colour and symbol indicate the health status and sensor state.",

    tree_map_title: "Interactive Tree Map",
    tree_map_body:
      "Click a tree pin to open the detail view. Use the layer control in the top right to switch between map and satellite view.",

    map_satellite_title: "Switch to Satellite View",
    map_satellite_body:
      "The layer control in the top right lets you switch to the satellite view, giving you a better understanding of the real surroundings of the trees. We will switch to satellite now.",

    map_add_tree_title: "Add a Tree",
    map_add_tree_body:
      "The Add Tree button in the bottom right starts the process of recording a new tree. Click the map to set the location, then fill in the form.",

    nav_sensors_title: "Sensor Monitoring",
    nav_sensors_body:
      "The sensor list shows all sensors of the project – with status, DevEUI/IMEI and last battery value.",

    sensor_list_title: "Sensor List",
    sensor_list_body:
      "Use search and sorting to quickly find suspicious sensors. Click a sensor to open the detail view with the last 10 measurements, threshold and battery status.",

    project_filter_title: "Global Project Filter",
    project_filter_body:
      "The project filter in the navigation bar narrows the view to a specific project. We will now select the first project for the data analysis.",

    nav_datascience_title: "Data Analysis",
    nav_datascience_body:
      "Analyse sensor time series (e.g. resistance, moisture index) over any time period and compare them with precipitation data.",

    datascience_filters_title: "Filters & Variables",
    datascience_filters_body:
      "The left filter panel shows areas, trees, sensors and variables. Step through each level to select what you want to analyse.",

    ds_filter_areas_title: "Step 1 – Select an Area",
    ds_filter_areas_body:
      "Tick one or more areas to load the trees within them. We will now select the first area.",

    ds_filter_trees_title: "Step 2 – Select a Tree",
    ds_filter_trees_body:
      "After selecting an area, the trees in that area appear. Choose one or more trees to see their sensors.",

    ds_filter_sensors_title: "Step 3 – Select a Sensor",
    ds_filter_sensors_body:
      "Select the sensor whose measurements you want to analyse. Each sensor provides specific variables.",

    ds_filter_variables_title: "Step 4 – Select a Variable",
    ds_filter_variables_body:
      "Enable one or more variables (e.g. resistance, moisture index) to display curves in the chart. Drag variables onto the left or right Y-axis.",

    datascience_date_title: "Time Range Filter",
    datascience_date_body:
      "On the right, select the analysis period: from 1 day to 1 year. Then click Load Data to update the charts.",

    ds_load_data_title: "Load Data",
    ds_load_data_body:
      "Load Data fetches all selected sensor measurements for the chosen time range and renders them in the chart. We will trigger the load now.",

    nav_tasks_title: "Tasks",
    nav_tasks_body:
      "Task management connects monitoring with operational implementation. List view for filtering/searching, calendar view for monthly planning.",

    task_header_title: "Tasks – Overview",
    task_header_body:
      "Filter tasks by type, period, status and priority. Search for specific tasks.",
    task_calendar_title: "Calendar View",
    task_calendar_body:
      "Switch to the calendar view to plan tasks in a monthly overview and visualise workload peaks.",
    task_add_title: "Create Task",
    task_add_body:
      "Click + New Task to create a task manually – e.g. irrigation, routine inspection or pruning. Select tree, type, date and responsible person.",

    nav_projects_title: "Projects",
    nav_projects_body:
      "Projects bundle trees, sensors and tasks. The project dashboard shows task distributions, tree phases and health charts.",

    projects_grid_title: "Projects Overview",
    projects_grid_body:
      "All your projects at a glance. Click View Project to open the project detail view with KPIs, map and data analysis.",

    projects_open_title: "Open a Project",
    projects_open_body:
      "We will now open the first project. The detail view contains a map, time series, tasks and all KPIs of the project – everything at a glance.",

    nav_settings_title: "Settings",
    nav_settings_body: "Manage notifications, language, users and customer data – all in Settings.",

    settings_page_title: "Settings – Overview",
    settings_page_body:
      "Configure email notifications, choose your language (German/English/Italian), manage users and update the customer logo.",

    nav_settings_users_title: "User Management",
    nav_settings_users_body:
      "Let us go to User Management, where you can create team members and define their access rights.",

    settings_users_title: "Add & Manage Users",
    settings_users_body:
      "Here you see all users of the account. Use + Add User to create new accounts.",

    settings_add_user_btn_title: "Add User",
    settings_add_user_btn_body:
      "Click + Add User to open the form. Enter name, email address and the desired persona.",

    user_persona_inspector_title: "Persona: Tree Inspector",
    user_persona_inspector_body:
      "This persona is designed for inspections and operational field work. It mainly uses tree cadastre, tasks and on-site documentation.",

    user_persona_manager_title: "Persona: City Tree Manager",
    user_persona_manager_body:
      "This persona is designed for management and overview workflows. It focuses on dashboards, analysis and strategic planning.",

    finish_title: "Tour Complete",
    finish_body:
      "You now know the key features of TreeSense Cloud. Use the manual for detailed step-by-step instructions. Good luck!",
    finish_close: "Finish Tour",
  },

  it: {
    welcome_title: "Benvenuto in TreeSense Cloud",
    welcome_body:
      "Questo tour vi mostrerà le funzionalità principali della piattaforma in pochi minuti – dalla panoramica all'analisi dei dati.",
    welcome_tip: "Potete chiudere il tour in qualsiasi momento con il pulsante ✕.",

    nav_dashboard_title: "Dashboard",
    nav_dashboard_body:
      "La dashboard raggruppa tutte le metriche principali in un colpo d'occhio. Riflette sempre il progetto selezionato nel Filtro Globale.",

    dashboard_overview_title: "Dashboard – Panoramica",
    dashboard_overview_body:
      "Vedete tutti i KPI del vostro inventario di alberi, sensori e attività in un unico posto.",
    dashboard_inventory_title: "Inventario Alberi",
    dashboard_inventory_body:
      "Numero di alberi e specie nel progetto corrente. Cliccate su Visualizza alberi per passare al catasto degli alberi.",
    dashboard_biometrics_title: "Biometria degli Alberi",
    dashboard_biometrics_body:
      "Valori medi per altezza, circonferenza del tronco, diametro della chioma ed età – utili per una rapida verifica della plausibilità dei dati.",
    dashboard_sensors_title: "Sensoristica & Stato",
    dashboard_sensors_body:
      "Numero di sensori e avvisi per stati critici (batteria scarica, sensori disconnessi). Cliccate su Visualizza sensori per accedere alla lista sensori.",
    dashboard_health_title: "Salute degli Alberi",
    dashboard_health_body:
      "Il grafico a ciambella mostra la distribuzione degli stati di salute degli alberi. Usate questa vista per dare priorità rapidamente a ispezioni e irrigazioni.",
    dashboard_tasks_title: "Attività per Periodo",
    dashboard_tasks_body:
      "Quante attività sono previste oggi, questa settimana o questo mese? Cliccate su Vai alle attività per aprire la gestione attività.",

    nav_trees_title: "Catasto Alberi",
    nav_trees_body:
      "Nel catasto degli alberi registrate e gestite tutti gli alberi – con posizione, dati dell'albero e assegnazione sensori.",

    tree_list_title: "Elenco Alberi",
    tree_list_body:
      "Tutti gli alberi del progetto selezionato in una tabella ricercabile e ordinabile. La colonna stato mostra lo stato di salute attuale.",
    tree_add_title: "Aggiungi Albero",
    tree_add_body:
      "Cliccate su + Aggiungi albero per creare un nuovo albero. Nel modulo selezionate progetto, area e inserite i dati più la posizione sulla mappa.",

    nav_map_title: "Mappa Alberi",
    nav_map_body:
      "La vista mappa mostra tutti gli alberi con la loro posizione su una mappa interattiva. Colore e simbolo indicano lo stato di salute e del sensore.",

    tree_map_title: "Mappa Interattiva degli Alberi",
    tree_map_body:
      "Cliccate su un segnaposto per aprire la vista dettaglio. In alto a destra potete passare tra vista mappa e satellite.",

    map_satellite_title: "Attiva Vista Satellite",
    map_satellite_body:
      "Il controllo layer in alto a destra consente di passare alla vista satellite, offrendo una migliore comprensione dell'ambiente reale degli alberi. Passiamo ora al satellite.",

    map_add_tree_title: "Aggiungi un Albero",
    map_add_tree_body:
      "Il pulsante Aggiungi albero in basso a destra avvia il processo di registrazione di un nuovo albero. Cliccate sulla mappa per impostare la posizione, poi compilate il modulo.",

    nav_sensors_title: "Monitoraggio Sensori",
    nav_sensors_body:
      "La lista sensori mostra tutti i sensori del progetto – con stato, DevEUI/IMEI e ultimo valore batteria.",

    sensor_list_title: "Elenco Sensori",
    sensor_list_body:
      "Usate ricerca e ordinamento per trovare rapidamente sensori anomali. Cliccate su un sensore per aprire la vista dettaglio con le ultime 10 misurazioni, soglia e stato batteria.",

    project_filter_title: "Filtro Progetto Globale",
    project_filter_body:
      "Il filtro progetto nella barra di navigazione limita la vista a un progetto specifico. Ora selezioniamo il primo progetto per analisi dati.",

    nav_datascience_title: "Analisi Dati",
    nav_datascience_body:
      "Analizzate serie temporali dei sensori (es. resistenza, indice di umidità) su qualsiasi periodo e confrontatele con i dati di precipitazione.",

    datascience_filters_title: "Filtri & Variabili",
    datascience_filters_body:
      "Il pannello filtri a sinistra mostra aree, alberi, sensori e variabili. Selezionate passo dopo passo ciò che volete analizzare.",

    ds_filter_areas_title: "Passo 1 – Seleziona un area",
    ds_filter_areas_body:
      "Spuntate una o più aree per caricare gli alberi al loro interno. Ora selezioniamo la prima area.",

    ds_filter_trees_title: "Passo 2 – Seleziona un albero",
    ds_filter_trees_body:
      "Dopo aver selezionato un area, appaiono gli alberi di quell'area. Scegliete uno o più alberi per vedere i loro sensori.",

    ds_filter_sensors_title: "Passo 3 – Seleziona un sensore",
    ds_filter_sensors_body:
      "Selezionate il sensore di cui volete analizzare le misurazioni. Ogni sensore fornisce variabili specifiche.",

    ds_filter_variables_title: "Passo 4 – Seleziona una variabile",
    ds_filter_variables_body:
      "Attivate una o più variabili (es. resistenza, indice di umidità) per visualizzare le curve nel grafico. Trascinate le variabili sull'asse Y sinistro o destro.",

    datascience_date_title: "Filtro Periodo",
    datascience_date_body:
      "A destra selezionate il periodo di analisi: da 1 giorno a 1 anno. Poi cliccate su Carica dati per aggiornare i grafici.",

    ds_load_data_title: "Carica dati",
    ds_load_data_body:
      "Carica dati recupera tutte le misurazioni dei sensori selezionati per il periodo scelto e le visualizza nel grafico. Avviamo ora il caricamento.",

    nav_tasks_title: "Attività",
    nav_tasks_body:
      "La gestione attività collega monitoraggio e implementazione operativa. Vista elenco per filtri/ricerca, vista calendario per la pianificazione mensile.",

    task_header_title: "Attività – Panoramica",
    task_header_body:
      "Filtrate le attività per tipo, periodo, stato e priorità. Cercate attività specifiche.",
    task_calendar_title: "Vista Calendario",
    task_calendar_body:
      "Passate alla vista calendario per pianificare le attività in una panoramica mensile e visualizzare i picchi di carico.",
    task_add_title: "Crea Attività",
    task_add_body:
      "Cliccate su + Nuova attività per creare manualmente un attività – es. irrigazione, ispezione di routine o potatura. Selezionate albero, tipo, data e persona responsabile.",

    nav_projects_title: "Progetti",
    nav_projects_body:
      "I progetti raggruppano alberi, sensori e attività. La dashboard del progetto mostra distribuzioni attività, fasi degli alberi e grafici di salute.",

    projects_grid_title: "Panoramica Progetti",
    projects_grid_body:
      "Tutti i vostri progetti in un colpo d'occhio. Cliccate su Visualizza progetto per aprire la vista dettaglio con KPI, mappa e analisi dati.",

    projects_open_title: "Apri un Progetto",
    projects_open_body:
      "Ora apriamo il primo progetto. La vista dettaglio contiene mappa, serie temporali, attività e tutti i KPI del progetto – tutto in un colpo d'occhio.",

    nav_settings_title: "Impostazioni",
    nav_settings_body:
      "Gestite notifiche, lingua, utenti e dati del cliente – tutto nelle Impostazioni.",

    settings_page_title: "Impostazioni – Panoramica",
    settings_page_body:
      "Configurate le notifiche email, scegliete la lingua (Tedesco/Inglese/Italiano), gestite gli utenti e aggiornate il logo cliente.",

    nav_settings_users_title: "Gestione Utenti",
    nav_settings_users_body:
      "Passiamo alla gestione utenti, dove potete creare membri del team e impostare i loro diritti di accesso.",

    settings_users_title: "Aggiungere e gestire utenti",
    settings_users_body:
      "Qui vedete tutti gli utenti del conto. Usate + Aggiungi utente per creare nuovi account.",

    settings_add_user_btn_title: "Aggiungi utente",
    settings_add_user_btn_body:
      "Cliccate su + Aggiungi utente per aprire il modulo. Inserite nome, indirizzo email e la persona desiderata.",

    user_persona_inspector_title: "Persona: Tree Inspector",
    user_persona_inspector_body:
      "Questa persona è pensata per ispezioni e lavoro operativo sul campo. Usa soprattutto catasto alberi, attività e documentazione sul posto.",

    user_persona_manager_title: "Persona: City Tree Manager",
    user_persona_manager_body:
      "Questa persona è pensata per gestione e visione d'insieme. Si concentra su dashboard, analisi e pianificazione strategica.",

    finish_title: "Tour Completato",
    finish_body:
      "Ora conoscete le funzionalità principali di TreeSense Cloud. Usate il manuale per istruzioni dettagliate passo dopo passo. Buona fortuna!",
    finish_close: "Fine Tour",
  },
};

export default CONTENT;
